import { useCallback, useEffect, useRef, useState } from "react";

const INACTIVITY_EVENTS = ["mousedown", "touchstart", "keydown", "wheel"] as const;
// 子要素の stopPropagation に影響されないよう capture で聞く
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };
// 予告中の操作に続く click を予告側で受け止めてから閉じる (下のボタンの誤操作防止)
const WARNING_DISMISS_GRACE_MS = 600;
const COUNTDOWN_TICK_MS = 250;

export type InactivityTimerState = {
  /** 予告表示中の残り秒数。予告中でなければ null */
  secondsLeft: number | null;
  /** 予告を閉じる (予告上のタップから呼ぶ) */
  dismissWarning: () => void;
};

/**
 * 無操作が timeoutMs 続いたら onTimeout を呼び、最後の warningMs の間は残り秒数を返す。
 * タイマーは操作があって初めて動き、発火後は次の操作まで止まる。
 */
export function useInactivityTimer(
  onTimeout: () => void,
  timeoutMs: number,
  enabled: boolean,
  warningMs: number = 0,
): InactivityTimerState {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let deadline = 0;
    let isWarning = false;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
    let warningTimer: ReturnType<typeof setTimeout> | null = null;
    let dismissTimer: ReturnType<typeof setTimeout> | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;

    const clearTimers = () => {
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (warningTimer) clearTimeout(warningTimer);
      if (countdownTimer) clearInterval(countdownTimer);
      timeoutTimer = warningTimer = countdownTimer = null;
    };

    const updateCountdown = () => {
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    };

    const arm = () => {
      clearTimers();
      if (isWarning) {
        isWarning = false;
        if (dismissTimer) clearTimeout(dismissTimer);
        dismissTimer = setTimeout(() => setSecondsLeft(null), WARNING_DISMISS_GRACE_MS);
      }
      deadline = Date.now() + timeoutMs;
      if (warningMs > 0 && warningMs < timeoutMs) {
        warningTimer = setTimeout(() => {
          isWarning = true;
          if (dismissTimer) clearTimeout(dismissTimer);
          updateCountdown();
          countdownTimer = setInterval(updateCountdown, COUNTDOWN_TICK_MS);
        }, timeoutMs - warningMs);
      }
      timeoutTimer = setTimeout(() => {
        clearTimers();
        isWarning = false;
        setSecondsLeft(null);
        onTimeoutRef.current();
      }, timeoutMs);
    };

    INACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, arm, LISTENER_OPTIONS));

    return () => {
      clearTimers();
      if (dismissTimer) clearTimeout(dismissTimer);
      setSecondsLeft(null);
      INACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, arm, LISTENER_OPTIONS),
      );
    };
  }, [enabled, timeoutMs, warningMs]);

  const dismissWarning = useCallback(() => setSecondsLeft(null), []);

  return { secondsLeft, dismissWarning };
}
